<template>
  <component
    :is="tag"
    class="inline-editable-text"
    :class="rootClasses"
  >
    <button
      v-if="!isEditing"
      type="button"
      class="inline-editable-text--trigger"
      :disabled="disabled || isSaving"
      @click="onEnterEdit"
    >
      <span
        class="inline-editable-text--value"
        :class="{ 'is-placeholder': !displayValue }"
      >{{ displayValue || placeholder }}</span>
      <Tooltip
        v-if="tooltip"
        position="top-left"
      >
        {{ tooltip }}
      </Tooltip>
    </button>

    <textarea
      v-else-if="multiline"
      ref="input-ref"
      v-model="draftValue"
      v-auto-resize
      class="inline-editable-text--input"
      rows="1"
      :placeholder="placeholder"
      :aria-label="ariaLabel"
      @keydown.esc.prevent="onCancel"
      @keydown="onTextareaKeydown"
      @blur="onCommit"
    />
    <input
      v-else
      ref="input-ref"
      v-model="draftValue"
      type="text"
      class="inline-editable-text--input"
      :placeholder="placeholder"
      :aria-label="ariaLabel"
      @keydown.enter.prevent="onCommit"
      @keydown.esc.prevent="onCancel"
      @blur="onCommit"
    >
  </component>
</template>

<script setup>
import { ref, computed, watch, nextTick, useTemplateRef } from 'vue'
import { cx } from '@/helpers'

defineOptions({ name: 'InlineEditableText' })
const emit = defineEmits(['commit', 'cancel'])
const props = defineProps({
  modelValue: { type: String, default: '' },
  tag: { type: String, default: 'div' },
  multiline: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
  ariaLabel: { type: String, required: true },
  tooltip: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false }
})

const inputRef = useTemplateRef('input-ref')

const isEditing = ref(false)
const displayValue = ref(props.modelValue)
const draftValue = ref('')
const preCommitValue = ref('')

const rootClasses = computed(() => cx(
  props.multiline && 'is-multiline',
  props.disabled && 'is-disabled',
  props.isSaving && 'is-saving'
))

const focusInput = () => {
  nextTick(() => {
    inputRef.value?.focus()
    inputRef.value?.select()
  })
}

const onEnterEdit = () => {
  draftValue.value = displayValue.value
  isEditing.value = true
  focusInput()
}
const onCancel = () => {
  isEditing.value = false
  emit('cancel')
}
const onCommit = () => {
  if (!isEditing.value) return

  const trimmed = draftValue.value.trim()

  if (props.required && !trimmed) {
    isEditing.value = false
    return
  }
  if (trimmed === displayValue.value) {
    isEditing.value = false
    return
  }

  preCommitValue.value = displayValue.value
  displayValue.value = trimmed
  isEditing.value = false
  emit('commit', trimmed)
}
const onTextareaKeydown = event => {
  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
    event.preventDefault()
    onCommit()
  }
}

watch(() => props.modelValue, val => {
  if (!isEditing.value) displayValue.value = val
})

const revertToEdit = () => {
  draftValue.value = displayValue.value
  displayValue.value = preCommitValue.value
  isEditing.value = true
  focusInput()
}

defineExpose({ revertToEdit })
</script>

<style scoped>
@reference "#app-styles.css";

.inline-editable-text {
  @apply relative;
}

.inline-editable-text--trigger {
  @apply flex items-baseline w-full text-left cursor-text;
  @apply bg-transparent border-0 p-0 m-0;
  @apply -mx-1.5 -my-0.5 px-1.5 py-0.5 rounded-md;
  @apply transition-colors duration-150 ease-out;
  font: inherit;
  color: inherit;
  letter-spacing: inherit;

  &:hover,
  &:focus-visible {
    @apply layer-highlight-faint;
  }

  &:focus-visible {
    @apply outline-2 outline-offset-2 rounded-sm;
  }

  &:disabled {
    @apply cursor-default;
  }
}

.inline-editable-text--value.is-placeholder {
  @apply ink-subtle font-normal;
}

.inline-editable-text--input {
  @apply block w-full resize-none bg-transparent border-0 outline-0;
  @apply -mx-1.5 -my-0.5 px-1.5 py-0.5 rounded-md;
  @apply layer-float;
  font: inherit;
  color: inherit;
  letter-spacing: inherit;
}

.inline-editable-text.is-saving .inline-editable-text--trigger {
  @apply opacity-60;
}
</style>
